/**
 * Frontend Constants
 *
 * Centralized constants to avoid magic numbers throughout the codebase.
 */

// Occasion type defined here to avoid circular dependency with StoreHours/types.ts
type OccasionTypeConstant =
  | 'DELIVERY'
  | 'CARRYOUT'
  | 'CATERING_CARRYOUT';

// Available occasions - configurable per deployment via environment variable
// For TB UK: Drive-Thru and Dine In have no effect on POS/kiosk (confirmed via testing).
// Only CARRYOUT is active; DELIVERY is auto-updated from CARRYOUT by Byte Graph.
const DEFAULT_OCCASIONS: OccasionTypeConstant[] = ['CARRYOUT'];
const VALID_OCCASIONS: OccasionTypeConstant[] = [
  'CARRYOUT',
  'DELIVERY',
  'CATERING_CARRYOUT',
];

function parseOccasionsFromEnv(): OccasionTypeConstant[] {
  const envValue = import.meta.env.VITE_AVAILABLE_OCCASIONS;

  if (!envValue || typeof envValue !== 'string') {
    return DEFAULT_OCCASIONS;
  }

  const parsed = envValue.split(',').map((s) => s.trim().toUpperCase()) as OccasionTypeConstant[];
  const filtered = parsed.filter((o) => VALID_OCCASIONS.includes(o));

  return filtered.length > 0 ? filtered : DEFAULT_OCCASIONS;
}

/**
 * Available occasions for this deployment.
 * Configured via VITE_AVAILABLE_OCCASIONS environment variable.
 * Example: VITE_AVAILABLE_OCCASIONS=CARRYOUT,DELIVERY,CATERING_CARRYOUT
 */
export const AVAILABLE_OCCASIONS: OccasionTypeConstant[] = parseOccasionsFromEnv();

// Time constants (in milliseconds)
export const ONE_MINUTE_IN_MS = 60 * 1000;
export const FIVE_MINUTES_IN_MS = 5 * 60 * 1000;
export const ONE_HOUR_IN_MS = 60 * 60 * 1000;
export const ONE_DAY_IN_MS = 24 * 60 * 60 * 1000;

// Minutes in a day (for time calculations)
export const MINUTES_IN_DAY = 24 * 60;

// Query stale times
export const STALE_TIME_DEFAULT = FIVE_MINUTES_IN_MS;
export const STALE_TIME_STORES = FIVE_MINUTES_IN_MS;
// Published menus change rarely (menu experience rollouts) — cache aggressively.
export const STALE_TIME_LIVE_MENU = 15 * ONE_MINUTE_IN_MS;

// Default expiry for item blackouts (24 hours from now)
export function getDefaultItemBlackoutExpiry(): Date {
  return new Date(Date.now() + ONE_DAY_IN_MS);
}

// Minimum valid expiry (5 minutes from now)
export function getMinimumValidExpiry(): Date {
  return new Date(Date.now() + FIVE_MINUTES_IN_MS);
}
